import React from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { useBookingDraft } from "../BookingDraftContext";
import { useServiceCatalog } from "@/hooks/services/useServiceCatalog";
import { salonNow } from "@/lib/time";
import WaitlistPanel from "./WaitlistPanel";

/**
 * Waiting-list dialog for the specialist step (waitlist README §4.1
 * rewire): entries are per-service, so a master-first client must pick
 * the service to wait for; a service-first client goes straight to the
 * form. No calendar step involved.
 *
 * The dialog loads the service catalog itself: a master-first flow may
 * open it before the services step has ever run, so the ServiceRegistry
 * index would otherwise be empty. Services for the master are derived
 * from the fetched snapshot via each service's `staffIds`.
 */
export default function WaitlistDialog({
  staffId,
  staffName,
  onClose,
}: {
  staffId: number;
  staffName: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { draft } = useBookingDraft();

  // Ensure the catalog is loaded regardless of how we got here.
  const { data: snap, isLoading: catalogLoading } = useServiceCatalog({ enabled: true });

  const services = React.useMemo(() => {
    const all = snap?.services ?? {};
    return (Object.values(all) as Array<{ id: number; title: string; staffIds?: number[] }>)
      .filter((s) => Array.isArray(s.staffIds) && s.staffIds.includes(Number(staffId)))
      .map((s) => ({ id: Number(s.id), title: s.title }));
  }, [snap, staffId]);

  const masterServiceIds = React.useMemo(
    () => services.map((s) => s.id),
    [services],
  );

  // service-first flow: the drafted service skips the picker entirely
  const drafted = Number(draft.serviceIds?.[0]);
  const [serviceId, setServiceId] = React.useState<number | null>(null);

  // Once the catalog resolves, auto-select the drafted service if the
  // master offers it (service-first path).
  React.useEffect(() => {
    if (serviceId != null) return;
    if (Number.isFinite(drafted) && masterServiceIds.includes(drafted)) {
      setServiceId(drafted);
    }
  }, [drafted, masterServiceIds, serviceId]);

  const now = salonNow();
  const defaultFrom = now.toISODate();
  const defaultTo = now.plus({ days: 30 }).toISODate();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div
        className="relative z-10 w-full max-w-sm max-h-[85vh] overflow-y-auto rounded-2xl bg-card p-4 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-start justify-between">
          <div>
            <h3 className="text-lg font-semibold text-foreground">
              {t("widget.waitlist.dialog_title", "Join the waiting list")}
            </h3>
            <p className="text-sm text-muted-foreground">{staffName}</p>
          </div>
          <button
            className="rounded-full p-1 text-muted-foreground hover:text-foreground"
            onClick={onClose}
            aria-label={t("widget.common.close", "Close")}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {catalogLoading ? (
          <div className="py-6 text-center text-sm text-muted-foreground">
            {t("widget.common.loading", "Loading...")}
          </div>
        ) : serviceId == null ? (
          <div>
            <p className="mb-2 text-sm text-muted-foreground">
              {t("widget.waitlist.pick_service", "Which service do you want to wait for?")}
            </p>
            <div className="space-y-2">
              {services.map((s) => (
                <button
                  key={s.id}
                  className="w-full rounded-xl border p-3 text-left hover:bg-muted/40"
                  onClick={() => setServiceId(Number(s.id))}
                >
                  <div className="text-sm font-medium text-foreground">{s.title}</div>
                </button>
              ))}
              {services.length === 0 && (
                <div className="text-sm text-muted-foreground">
                  {t("widget.waitlist.no_services", "This master has no bookable services configured.")}
                </div>
              )}
            </div>
          </div>
        ) : (
          <>
            {services.length > 1 && (
              <button
                className="mb-2 text-xs text-muted-foreground underline"
                onClick={() => setServiceId(null)}
              >
                {t("widget.waitlist.change_service", "← choose another service")}
              </button>
            )}
            <div className="mb-2 text-sm font-medium text-foreground">
              {snap?.services?.[serviceId]?.title}
            </div>
            <WaitlistPanel
              serviceId={serviceId}
              staffId={Number(staffId)}
              defaultFrom={defaultFrom}
              defaultTo={defaultTo}
            />
          </>
        )}
      </div>
    </div>
  );
}
